import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ConfigurationTargetSettingsSliceState = {
    aggregateWaitTimeInSeconds: number | null;
    originalAggregateWaitTimeInSeconds: number | null;
    isLoading: boolean;
    isSaving: boolean;
    fetchError: string | null;
    saveError: string | null;
};

export const initialState: ConfigurationTargetSettingsSliceState = {
    aggregateWaitTimeInSeconds: null,
    originalAggregateWaitTimeInSeconds: null,
    isLoading: false,
    isSaving: false,
    fetchError: null,
    saveError: null,
};

const configurationTargetSettingsSlice = createSlice({
    name: 'configurationTargetSettings',
    initialState,
    reducers: {
        configurationTargetSettingsFetched(state) {
            state.isLoading = true;
            state.fetchError = null;
        },

        configurationTargetSettingsLoaded(state, action: PayloadAction<{ aggregateWaitTimeInSeconds: number | null, error?: string }>) {
            const { aggregateWaitTimeInSeconds, error } = action.payload;
            state.isLoading = false;
            if (error) {
                state.fetchError = error;
            } else {
                state.aggregateWaitTimeInSeconds = aggregateWaitTimeInSeconds;
                state.originalAggregateWaitTimeInSeconds = aggregateWaitTimeInSeconds;
                state.fetchError = null;
            }
        },

        aggregateWaitTimeChanged(state, action: PayloadAction<number | null>) {
            state.aggregateWaitTimeInSeconds = action.payload;
        },

        configurationTargetSettingsSaveStarted(state) {
            state.isSaving = true;
            state.saveError = null;
        },

        configurationTargetSettingsSaveFinished(state, action: PayloadAction<{ success: boolean, error?: string }>) {
            const { success, error } = action.payload;
            state.isSaving = false;
            if (success) {
                state.originalAggregateWaitTimeInSeconds = state.aggregateWaitTimeInSeconds;
                state.saveError = null;
            } else {
                state.saveError = error || 'Failed to save settings';
            }
        },

        configurationTargetSettingsReverted(state) {
            state.aggregateWaitTimeInSeconds = state.originalAggregateWaitTimeInSeconds;
            state.saveError = null;
        },
    },
    selectors: {
        selectAggregateWaitTime: (state) => state.aggregateWaitTimeInSeconds,
        selectOriginalAggregateWaitTime: (state) => state.originalAggregateWaitTimeInSeconds,
        selectHasUnsavedChanges: (state) => state.aggregateWaitTimeInSeconds !== state.originalAggregateWaitTimeInSeconds,
        selectIsLoading: (state) => state.isLoading,
        selectIsSaving: (state) => state.isSaving,
        selectFetchError: (state) => state.fetchError,
        selectSaveError: (state) => state.saveError,
    },
});

export const {
    configurationTargetSettingsFetched,
    configurationTargetSettingsLoaded,
    aggregateWaitTimeChanged,
    configurationTargetSettingsSaveStarted,
    configurationTargetSettingsSaveFinished,
    configurationTargetSettingsReverted,
} = configurationTargetSettingsSlice.actions;

export const { getInitialState, selectors: configurationTargetSettingsSelectors } = configurationTargetSettingsSlice;

export default configurationTargetSettingsSlice.reducer;
